import React, { useState } from 'react';
import {
  Typography,
  Box,
  useTheme,
  useMediaQuery,
  Drawer,
  IconButton,
  Button,
  Paper,
  Grid,
  Backdrop,
  CircularProgress,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { useProblem } from '../../hooks/useProblem';
import ProblemList from './ProblemList';
import ProblemCreate from './ProblemCreate';
import ProblemView from './ProblemView';
import { Problem } from '../../models/Problem';
import { StyledContainer } from './ProblemStyles';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux/store';
import { useNavigate, useLocation } from 'react-router-dom';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';

const Problems: React.FC = () => {
  const { loading, error } = useProblem();
  const [selectedProblem, setSelectedProblem] = useState<Problem | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const isAuthenticated = useSelector((state: RootState) => state.user.isAuthenticated);
  const navigate = useNavigate();
  const location = useLocation();

  // If the user is not authenticated, show a styled message and a button to redirect to login
  if (!isAuthenticated) {
    return (
      <Grid container justifyContent="center" alignItems="center">
        <Grid item xs={12} sm={6} md={4}>
          <Paper
            elevation={3}
            style={{ padding: '20px', textAlign: 'center', borderRadius: '12px' }}
          >
            <Box display="flex" justifyContent="center" alignItems="center" marginBottom="20px">
              <LockOutlinedIcon fontSize="large" color="primary" />
            </Box>
            <Typography variant="h4" gutterBottom>
              Sign In Required
            </Typography>
            <Typography variant="body1" gutterBottom>
              You need to sign in with your account to view the problems.
            </Typography>
            <Button
              onClick={() => navigate('/signin', { state: { from: location.pathname } })}
              variant="contained"
              color="primary"
              fullWidth
              style={{ marginTop: '20px', padding: '15px' }}
            >
              Go to Login
            </Button>
          </Paper>
        </Grid>
      </Grid>
    );
  }

  // Spinner and overlay during loading
  const loadingOverlay = (
    <Backdrop sx={{ color: '#fff', zIndex: theme.zIndex.drawer + 1 }} open={loading}>
      <CircularProgress color="inherit" />
    </Backdrop>
  );

  const problemList = (
    <ProblemList
      onSelectProblem={(problem: Problem) => {
        setSelectedProblem(problem);
        if (!isDesktop) setDrawerOpen(false);
      }}
    />
  );

  const mainContent = (
    <>
      {selectedProblem ? (
        <ProblemView problem={selectedProblem} onClose={() => setSelectedProblem(null)} />
      ) : (
        <ProblemCreate />
      )}
    </>
  );

  return (
    <StyledContainer>
      <Typography variant="h4" component="h1" gutterBottom>
        Problems
      </Typography>
      <Box display="flex">
        {isDesktop ? (
          <>
            <Drawer
              variant="permanent"
              sx={{
                width: 240,
                flexShrink: 0,
                '& .MuiDrawer-paper': {
                  width: 240,
                  boxSizing: 'border-box',
                  position: 'relative',
                },
              }}
            >
              {problemList}
            </Drawer>
            <Box flexGrow={1} ml={2}>
              {mainContent}
            </Box>
          </>
        ) : (
          <>
            <IconButton
              edge="start"
              color="inherit"
              aria-label="menu"
              onClick={() => setDrawerOpen(true)}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
            <Drawer anchor="left" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
              <Box sx={{ width: 250 }} role="presentation">
                {problemList}
              </Box>
            </Drawer>
            <Box flexGrow={1}>{mainContent}</Box>
          </>
        )}
      </Box>
      {/* Add loading overlay */}
      {loadingOverlay}
      {/* Display error if there is one */}
      {error && <Typography color="error">{error}</Typography>}
    </StyledContainer>
  );
};

export default Problems;
